import { Box, CircularProgress, Switch } from "@mui/material";
import { Typography, useMediaQuery } from "@mui/material";
import { ProjectsType } from "./types";
import { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext, UserDetailsContextInterface } from "../../contexts";
import { AuthContextInterface, UserDetailsContext } from "../../contexts";
import { styles } from "./styles";
import { getAllProjects } from "../../services/projectService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { GetEnterpriseAllUsersResponse, Role, User } from "../../proto/api_pb";
import { GetProjectsResponse, Project } from "../../proto/api_pb";
import { AddProjectModal } from "../../components/Modal/AddProjectModal";
import { SingleProject } from "../../components/SingleProject/SingleProject";
import { ProjectSearchBar } from "../../components/SingleProject/ProjectSearchBar";
import { ProjectsFilterMenu } from "./ProjectsFilterMenu";
import { ProjectsSortMenu } from "./ProjectsSortMenu";
import { ContainerForGrid } from "../../components/Exolore/styles";
import { MobileScreenBottomNavigationContainer } from "../Tracks/styles";
import { MobileScreenBottomNavigation } from "../../components/TracksPageComponents/MobileScreenBottomNavigation";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";
import { getEnterpriseAllUsers } from "../../services/enterpriseService";
import { MobileDrawer } from "../../components/MobileDrawer/MobileDrawer";
import { useNavigate } from "react-router-dom";
import { styles as tracksStyles } from "../Tracks/styles";
const label = { inputProps: { "aria-label": "Checkbox demo" } };

export const Projects: React.FC<ProjectsType> = ({
  session,
  setInbox,
  openFoldersList,
  setOpenFoldersList,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [queryValue, setQueryValue] = useState<string>("");
  const [userNames, setUserNames] = useState<string[]>([]);
  const [enterpriseUsers, setEnterpriseUsers] = useState<User[]>([]);
  const [selectedCollaborators, setSelectedCollaborators] =
    useState<string[]>();
  const [sortBy, setSortBy] = useState<string>("Last modified");
  const [isReorderAvailable, setIsReorderAvailable] = useState<boolean>(
    false
  );
  const [draggedProjectIndex, setDraggedProjectIndex] = useState<number | null>(
    null
  );
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [customOrder, setCustomOrder] = useState<Map<string, number>>(new Map());
  const { getValidAccessToken } = useGetValidAccessToken();
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const isMobileScreen = useMediaQuery("(max-width:900px)");
  const { page, scrollContainerRef, setIsScrollEnabled, setPage } =
    useInfiniteScroll();
  const { isEnterpriseAccount, enterprise } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);
  const navigate = useNavigate();

  const handleUseEffect = useCallback(
    async (refresh: boolean = false) => {
      if (!userId || !enterprise?.id || !enterprise.role) return;
      setIsLoading(true);

      if (refresh) {
        setPage(0);
        setProjects([]);
      }

      const token = await getValidAccessToken();
      const actualSortBy = isReorderAvailable ? "Custom Order" : sortBy;
      const sortOrder = actualSortBy === "Title" ? "asc" : "desc";
      const res = await getAllProjects(
        token,
        userId,
        queryValue,
        selectedCollaborators,
        enterprise?.id ?? "",
        page,
        Number(enterprise?.role) ?? 0,
        actualSortBy,
        sortOrder
      );

      if (res instanceof GetProjectsResponse) {
        let projectsList = res.getProjectsList();
        
        if (isReorderAvailable && customOrder.size > 0) {
          projectsList = [...projectsList].sort((a, b) => {
            const posA = customOrder.get(a.getId() || "") ?? Infinity;
            const posB = customOrder.get(b.getId() || "") ?? Infinity;
            if (posA === Infinity && posB === Infinity) return 0;
            if (posA === Infinity) return 1;
            if (posB === Infinity) return -1;
            return posA - posB;
          });
        }
        
        if (refresh) {
          setProjects(projectsList);
        } else {
          setProjects((prev) => {
            const existingProjectIds = new Set(
              prev.map((project) => project.getId())
            );
            const uniqueProjects = projectsList.filter(
              (project) => !existingProjectIds.has(project.getId())
            );
            return [...prev, ...uniqueProjects];
          });
        }

        if (res.getProjectsList().length < 8 && setIsScrollEnabled) {
          setIsScrollEnabled(false);
          if (setIsLoading) setIsLoading(false);
        } else if (setIsScrollEnabled) {
          setIsScrollEnabled(true);
        }
      }
      setIsLoading(false);
    },
    [
      enterprise?.id,
      enterprise?.role,
      getValidAccessToken,
      page,
      queryValue,
      selectedCollaborators,
      setIsScrollEnabled,
      setPage,
      userId,
      sortBy,
      customOrder,
      isReorderAvailable,
    ]
  );

  useEffect(() => {
    const savedOrder = localStorage.getItem(`projectCustomOrder_${enterprise?.id}_${userId}`);
    if (savedOrder) {
      try {
        const orderMap = new Map<string, number>(JSON.parse(savedOrder));
        setCustomOrder(orderMap);
      } catch (e) {
        console.error("Failed to load custom order:", e);
      }
    }
  }, [enterprise?.id, userId]);

  useEffect(() => {
    handleUseEffect();
  }, [handleUseEffect]);



  const handleDragStart = (index: number) => {
    setDraggedProjectIndex(index);
  };

  const handleDrop = async () => {
    if (isReorderAvailable) {
      if (draggedProjectIndex === null || draggedIndex === null) return;
      const updatedProjects = [...projects];
      const [draggedProject] = updatedProjects.splice(draggedProjectIndex, 1);
      updatedProjects.splice(draggedIndex, 0, draggedProject);
      setProjects([]);
      setDraggedProjectIndex(null);
      
      const newOrder = new Map<string, number>();
      updatedProjects.forEach((project, index) => {
        const projectId = project.getId();
        if (projectId) {
          newOrder.set(projectId, index);
        }
      });
      
      setCustomOrder(newOrder);
      const orderArray = Array.from(newOrder.entries());
      localStorage.setItem(`projectCustomOrder_${enterprise?.id}_${userId}`, JSON.stringify(orderArray));
      
      setProjects(updatedProjects);
    }
  };

  useEffect(() => {
    const handleUserNames = async () => {
      const token = await getValidAccessToken();
      const res = await getEnterpriseAllUsers(token, enterprise?.id ?? "");
      if (res instanceof GetEnterpriseAllUsersResponse) {
        const uName = res
          .getUsersList()
          .filter((user) => !user.getUsername()?.includes("dummy_ommuse"))
          .map((user) => user.getUsername() || user.getEmail());
        setUserNames(uName);
        setEnterpriseUsers(
          res
            .getUsersList()
            .filter((user) => !user.getUsername()?.includes("dummy_ommuse"))
        );
      }
    };
    handleUserNames();
  }, [enterprise?.id, getValidAccessToken]);

  if (!isEnterpriseAccount) {
    navigate("not-found");
    return <></>;
  }
  return (
    <>
      <MobileDrawer />
      <Box sx={{ height: "100vh", display: "flex" }}>
        <Box ref={scrollContainerRef} sx={styles.rightSideContainer}>
          <Box
            sx={{
              ...styles.projectsHeaderContainer,
              width: isMobileScreen ? "85vw" : "100%",
            }}
          >
            <Box sx={styles.headerContainer}>
              <Typography sx={{ fontWeight: "bold", fontSize: "30px" }}>
                Projects
              </Typography>
              {(Number(enterprise?.role) === Role.ROLE_ADMIN ||
                Number(enterprise?.role) === Role.ROLE_MANAGER) && (
                <AddProjectModal
                  enterpriseUsers={enterpriseUsers}
                  handleUseEffect={handleUseEffect}
                  setProjects={setProjects}
                  projects={projects}
                  setPage={setPage}
                />
              )}
            </Box>
            <Box sx={{ display: "flex", gap: "10px", marginTop: "10px", alignItems: "center", flexWrap: "wrap" }}>
              <ProjectSearchBar
                queryValue={queryValue}
                setQueryValue={setQueryValue}
                setPage={setPage}
                setProjects={setProjects}
              />
              <ProjectsFilterMenu
                userNames={userNames}
                setSelectedCollaborators={setSelectedCollaborators}
                setProjects={setProjects}
                setPage={setPage}
                selectedCollaborators={selectedCollaborators}
              />
              <ProjectsSortMenu
                setProjects={setProjects}
                setPage={setPage}
                sortBy={sortBy}
                setSortBy={setSortBy}
              />
              <Box sx={tracksStyles.reorderContainer}>
                <Switch
                  {...label}
                  checked={isReorderAvailable}
                  onChange={() => {
                    if (!isReorderAvailable) {
                      setPage(0);
                      setProjects([]);
                      setIsReorderAvailable(true);
                    } else {
                      setPage(0);
                      setProjects([]);
                      setIsReorderAvailable(false);
                    }
                  }}
                />
                Custom Order
              </Box>
            </Box>
          </Box>
          <ContainerForGrid
            sx={
              isMobileScreen ? { marginBottom: "90px" } : { marginTop: "15px" }
            }
          >
            {projects.map((project, index) => ( 
              <SingleProject
                key={index}
                index={index}
                project={project}
                session={session}
                setInbox={setInbox}
                handleUseEffect={handleUseEffect}
                openFoldersList={openFoldersList}
                setOpenFoldersList={setOpenFoldersList}
                isReorderAvailable={isReorderAvailable}
                handleDragStart={handleDragStart}
                handleDrop={handleDrop}
                setDraggedIndex={setDraggedIndex}
              />
            ))}
          </ContainerForGrid>
          {isLoading && (
            <Box sx={styles.loadingContainer}>
              <CircularProgress />
            </Box>
          )}
          {projects.length === 0 && !isLoading && (
            <Box sx={styles.emptyMessageContainer}>
              <Typography>No projects found!</Typography>
            </Box>
          )}
        </Box>
      </Box>
      <MobileScreenBottomNavigationContainer>
        <MobileScreenBottomNavigation />
      </MobileScreenBottomNavigationContainer>
    </>
  );
};
